import {
  DynamicForm,
  type DynamicFormFieldType,
  type DynamicFormRef,
  isDropDownResult,
  type JsonDataWrapper,
  type OdinDataRetrieval,
  type OdinDataRetrievalOptions,
  OdinDataSender,
  type OdinKey,
} from '@myosh/odin-components';
import clsx from 'clsx';
import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { useHierarchyTypesQuery } from '../../../../redux/services/hierarchy';
import {
  selectHierarchyEnableAdvance,
  selectSelectedHierarchy,
  setSelectedHierarchy,
  updateHierarchyFieldData,
} from '../../../../redux/slices/form-builder-slice';
import CloseActiveItemButton from '../../../common/close-active-item-button';
import { advancedHierarchyFormSettings } from './hierarchy-field.util';
import type { AdvancedHierarchyTypeConfig } from 'apps/web/src/@types/hierarchy-fields';

const AdvancedHierarchyForm = () => {
  const dispatch = useAppDispatch();
  const selectedHierarchyField = useAppSelector(selectSelectedHierarchy);
  const hierarchyEnableAdvance = useAppSelector(selectHierarchyEnableAdvance);

  const { data: hierarchyTypes, isFetching } = useHierarchyTypesQuery({ archived: 'false' });

  const [showAll, setShowAll] = useState<boolean>(false);

  const formIdRef = useRef(crypto.randomUUID());
  const dynamicFormRef = useRef<DynamicFormRef>(null);

  const selectedFieldRef = useRef(selectedHierarchyField);
  selectedFieldRef.current = selectedHierarchyField;

  const config = useMemo(
    () => (selectedHierarchyField ? selectedHierarchyField.hierarchyTypeConfigDTO : null),
    [selectedHierarchyField]
  );

  const upperLimitOptions = useMemo(() => {
    if (!hierarchyTypes || !selectedHierarchyField) return [];
    return cloneDeep(hierarchyTypes)
      .sort((a, b) => a.weight - b.weight)
      .filter((ht) => ht.weight < (selectedHierarchyField?.weight ?? 0))
      .map((ht) => ({ text: ht.caption ?? '', value: ht.id }));
  }, [hierarchyTypes, selectedHierarchyField]);

  const formSettings = useMemo(() => advancedHierarchyFormSettings(showAll), [showAll]);

  useEffect(() => {
    if (!isFetching && selectedHierarchyField) {
      setShowAll(Boolean(config?.showAll));
      dynamicFormRef.current?.resetFormState({});
      dynamicFormRef.current?.loadData({
        editable: config?.editable ?? false,
        useInitialValue: config?.useInitialValue ?? false,
        showAll: config?.showAll ?? false,
        upperBoundHierarchyTypeId:
          upperLimitOptions.find((opt) => opt.value === config?.upperBoundHierarchyTypeId) ?? null,
      });
    }
  }, [isFetching, selectedHierarchyField, config, upperLimitOptions]);

  const dataRetrieval: OdinDataRetrieval = {
    getData: (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
      if ('COMBOBOX' === options?.fieldType && options.page === 1) {
        subscriber.sendData({ data: upperLimitOptions });
      } else {
        subscriber.sendData();
      }
    },
  };

  const onCloseHierarchyDetailsClick = useCallback(() => {
    dispatch(setSelectedHierarchy(undefined));
  }, [dispatch]);

  const handleChange = useCallback(
    (_fieldId: OdinKey, fieldName: string, _fieldType: DynamicFormFieldType, fieldValue: unknown) => {
      if (!selectedFieldRef.current) {
        return;
      }
      const value = isDropDownResult(fieldValue) ? fieldValue.value : fieldValue;
      const currentConfig = dynamicFormRef.current?.getData() as AdvancedHierarchyTypeConfig;

      if (fieldName === 'showAll') {
        setShowAll(!!value);
        if (value === false) {
          currentConfig.upperBoundHierarchyTypeId = undefined;
          dynamicFormRef.current?.setFieldValue('upperBoundHierarchyTypeId', undefined);
        }
      }

      dispatch(
        updateHierarchyFieldData({
          ...selectedFieldRef.current,
          hierarchyTypeConfigDTO: {
            ...currentConfig,
            [fieldName]: value,
          },
        })
      );
    },
    [dispatch]
  );

  return (
    <div className="flex w-full flex-col">
      <div className="flex items-center justify-between py-2">
        <h2 className="text-lg font-semibold">Hierarchy Details</h2>
        <CloseActiveItemButton onClick={onCloseHierarchyDetailsClick} />
      </div>
      <div
        className={clsx('', {
          'cursor-not-allowed opacity-50': !hierarchyEnableAdvance,
        })}
      >
        {formSettings && (
          <DynamicForm
            key={selectedHierarchyField?.id ?? formIdRef.current}
            ref={dynamicFormRef}
            dynamicFormId={formIdRef.current}
            dataRetrieval={dataRetrieval}
            settings={formSettings}
            onSubmit={() => {}}
            data={{}}
            readOnly={!hierarchyEnableAdvance || !selectedHierarchyField?.visible}
            onFieldChanged={handleChange}
            formMode="all"
          />
        )}
      </div>
    </div>
  );
};

export default React.memo(AdvancedHierarchyForm);
